import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  createEvent,
  updateEvent,
  getAllEvents,
  deleteEvent,
  getEventTickets,
  updateTicketAttendance,
} from "../../firebase";
import { createOrUpdateStripeProduct } from "../../stripe";
import { Event, Ticket } from "../../types";
import { useAuth } from "../../contexts/AuthContext";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  MapPinIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  PhotoIcon,
  LinkIcon,
  DocumentTextIcon,
  ListBulletIcon,
  QueueListIcon,
  XMarkIcon,
  CheckIcon,
  ArrowPathIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";

interface FormData {
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  locationURL: string;
  price: string;
  status: string;
}

interface FormErrors {
  title: string;
  description: string;
  date: string;
}

interface TicketWithAttendee extends Ticket {
  attendee?: {
    name: string;
    email: string;
    company?: string;
  };
}

const EventManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [formData, setFormData] = useState<FormData>({
    title: "",
    description: "",
    date: "",
    time: "",
    location: "",
    locationURL: "",
    price: "",
    status: "open",
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({
    title: "",
    description: "",
    date: "",
  });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [attendeesDialogOpen, setAttendeesDialogOpen] = useState(false);
  const [attendees, setAttendees] = useState<TicketWithAttendee[]>([]);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  
  // Email functionality
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState({
    title: "",
    message: "",
  });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const fetchedEvents = await getAllEvents();
      setEvents(fetchedEvents);
      setError(null);
    } catch (err) {
      console.error("Failed to load events:", err);
      setError("Failed to load events. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (event?: Event) => {
    if (event) {
      setSelectedEvent(event);
      setFormData({
        title: event.title,
        description: event.description,
        date: event.date,
        time: event.time,
        location: event.location,
        locationURL: event.locationURL,
        price: event.price,
        status: event.status,
      });
      setPreviewUrl(event.imageURL || "");
    } else {
      setSelectedEvent(null);
      setFormData({
        title: "",
        description: "",
        date: "",
        time: "",
        location: "",
        locationURL: "",
        price: "",
        status: "open",
      });
      setPreviewUrl("");
      setImage(null);
    }
    setFormErrors({
      title: "",
      description: "",
      date: "",
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImage(file);

      // Create preview URL
      const fileReader = new FileReader();
      fileReader.onload = () => {
        if (fileReader.result) {
          setPreviewUrl(fileReader.result.toString());
        }
      };
      fileReader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    let valid = true;
    const errors = {
      title: "",
      description: "",
      date: "",
    };

    if (!formData.title.trim()) {
      errors.title = "Title is required";
      valid = false;
    }

    if (!formData.description.trim()) {
      errors.description = "Description is required";
      valid = false;
    }

    if (!formData.date) {
      errors.date = "Date is required";
      valid = false;
    }

    setFormErrors(errors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      let eventId: string;

      if (selectedEvent) {
        // Update existing event
        await updateEvent(
          selectedEvent.id,
          {
            title: formData.title,
            description: formData.description,
            date: formData.date,
            time: formData.time,
            location: formData.location,
            locationURL: formData.locationURL,
            price: formData.price,
            status: formData.status as "open" | "closed" | "hidden",
          },
          image
        );
        eventId = selectedEvent.id;
      } else {
        // Create new event
        if (!currentUser) {
          throw new Error("You must be logged in to create an event");
        }

        eventId = await createEvent(
          formData.title,
          formData.description,
          image,
          formData.date,
          formData.time,
          formData.location,
          formData.locationURL,
          formData.price,
          formData.status as "open" | "closed" | "hidden",
          currentUser.uid
        );
      }

      // Create Stripe product for this event ONLY if it doesn't already have one
      if (formData.status !== "hidden" && parseFloat(formData.price) > 0) {
        const hasExistingProduct = selectedEvent?.stripeProductId;
        
        if (!hasExistingProduct) {
          try {
            console.log(`Creating Stripe product for event: ${eventId} (missing product)`);
            await createOrUpdateStripeProduct(eventId);
            console.log(`Successfully created Stripe product for event: ${eventId}`);
          } catch (stripeError) {
            console.error("Failed to create Stripe product:", stripeError);
            setError(`Event saved but Stripe product creation failed: ${stripeError instanceof Error ? stripeError.message : String(stripeError)}`);
            // Continue even if Stripe product creation fails
          }
        } else {
          console.log(`Event ${eventId} already has Stripe product: ${hasExistingProduct} - skipping creation`);
        }
      } else {
        console.log(`Skipping Stripe product creation for event ${eventId} - Status: ${formData.status}, Price: ${formData.price}`);
      }

      await loadEvents();
      handleCloseDialog();
    } catch (err) {
      console.error("Failed to save event:", err);
      setError("Failed to save event. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (event: Event) => {
    setSelectedEvent(event);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedEvent) return;

    try {
      setLoading(true);
      await deleteEvent(selectedEvent.id);
      await loadEvents();
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error("Failed to delete event:", err);
      setError("Failed to delete event. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // Text formatting functions
  const formatText = (
    startTag: string,
    endTag: string,
    defaultText: string = "text"
  ) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const textToInsert = selectedText || defaultText;

    const newText =
      textarea.value.substring(0, start) +
      startTag +
      textToInsert +
      endTag +
      textarea.value.substring(end);

    setFormData({
      ...formData,
      description: newText,
    });

    // Set cursor position after update
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + startTag.length,
        start + startTag.length + textToInsert.length
      );
    }, 0);
  };

  const formatBold = () => formatText("<strong>", "</strong>", "bold text");
  const formatItalic = () => formatText("<em>", "</em>", "italic text");
  const formatHeading = () => formatText("<h3>", "</h3>", "Heading");

  const insertBulletList = () => {
    formatText(
      "<ul>\n  <li>",
      "</li>\n  <li>Item 2</li>\n  <li>Item 3</li>\n</ul>",
      "Item 1"
    );
  };

  const insertNumberedList = () => {
    formatText(
      "<ol>\n  <li>",
      "</li>\n  <li>Item 2</li>\n  <li>Item 3</li>\n</ol>",
      "Item 1"
    );
  };

  const insertLink = () => {
    const url = prompt("Enter the URL:", "https://");
    if (url) {
      formatText(`<a href="${url}" target="_blank">`, "</a>", "link text");
    }
  };

  const insertImage = () => {
    const url = prompt("Enter the image URL:", "https://");
    if (url) {
      formatText(
        `<img src="${url}" alt="`,
        '" style="max-width: 100%; height: auto;" />',
        "Image description"
      );
    }
  };

  const handleOpenAttendeesDialog = async (event: Event) => {
    setSelectedEvent(event); // Set the selected event for email functionality
    setAttendeesLoading(true);
    try {
      const tickets = await getEventTickets(event.id);
      setAttendees(tickets as TicketWithAttendee[]);
      setAttendeesDialogOpen(true);
    } catch (error) {
      console.error("Failed to load attendees:", error);
    } finally {
      setAttendeesLoading(false);
    }
  };

  const handleCloseAttendeesDialog = () => {
    setAttendeesDialogOpen(false);
  };

  const toggleAttendance = async (
    ticketId: string,
    currentAttended: boolean
  ) => {
    try {
      // Update attendance status in Firestore
      await updateTicketAttendance(ticketId, !currentAttended);

      // Update local state to reflect the change
      setAttendees((prevAttendees) =>
        prevAttendees.map((ticket) =>
          ticket.id === ticketId
            ? {
                ...ticket,
                attended: !currentAttended,
                status: !currentAttended ? "used" : "active",
              }
            : ticket
        )
      );
    } catch (error) {
      console.error("Failed to update attendance status:", error);
      // Show error notification if needed
    }
  };

  const handleOpenEmailDialog = () => {
    // Pre-populate with event info
    console.log('selectedEvent', selectedEvent);
    const eventTitle = selectedEvent?.title || "Event";
    setEmailData({
      title: `Update: ${eventTitle}`,
      message: ``,
    });
    setEmailError(null);
    setEmailSuccess(null);
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
  };

  const handleSendEmail = async () => {
    if (!emailData.title.trim() || !emailData.message.trim()) {
      setEmailError("Please provide both a title and message.");
      return;
    }

    // Get unique email addresses from attendees
    const emailAddresses = Array.from(new Set(
      attendees
        .map(ticket => ticket.email)
        .filter(email => email && email.trim() !== "")
    ));

    if (emailAddresses.length === 0) {
      setEmailError("No valid email addresses found among attendees.");
      return;
    }

    setEmailLoading(true);
    setEmailError(null);
    setEmailSuccess(null);

    try {
      const functions = getFunctions();
      const sendGeneralEmailFunction = httpsCallable(functions, 'sendGeneralEmailFunction');
      
      const result = await sendGeneralEmailFunction({
        title: emailData.title,
        message: emailData.message,
        emailAddresses: emailAddresses
      });

      setEmailSuccess(`Email sent successfully to ${emailAddresses.length} attendees!`);
      
      // Close dialog after a short delay
      setTimeout(() => {
        setEmailDialogOpen(false);
        setEmailSuccess(null);
      }, 2000);

    } catch (error: any) {
      console.error("Error sending email:", error);
      setEmailError(error.message || "Failed to send email. Please try again.");
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Event Management</h1>
        <button
          onClick={() => handleOpenDialog()}
          className="flex items-center px-4 py-2 bg-gold hover:bg-gold/90 text-white rounded-md shadow-sm"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Event
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <ArrowPathIcon className="h-8 w-8 text-gold animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <div
              key={event.id}
              className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col h-full"
            >
              {event.imageURL ? (
                <div
                  className="h-48 bg-cover bg-center"
                  style={{ backgroundImage: `url(${event.imageURL})` }}
                />
              ) : (
                <div className="h-48 bg-gray-200 flex justify-center items-center">
                  <CalendarIcon className="h-12 w-12 text-gray-400" />
                </div>
              )}

              <div className="p-4 flex-grow">
                <h2 className="text-xl font-semibold mb-2 text-gray-900">
                  {event.title}
                </h2>
                <div
                  className="text-gray-600 mb-4 line-clamp-3"
                  dangerouslySetInnerHTML={{
                    __html:
                      event.description
                        .replace(/<[^>]*>/g, "")
                        .substring(0, 150) + "...",
                  }}
                />

                <div className="space-y-2 text-sm">
                  <div className="flex items-start">
                    <CalendarIcon className="h-5 w-5 text-gray-500 mr-2 mt-0.5" />
                    <span>{event.date}</span>
                  </div>

                  {event.time && (
                    <div className="flex items-start">
                      <ClockIcon className="h-5 w-5 text-gray-500 mr-2 mt-0.5" />
                      <span>{event.time}</span>
                    </div>
                  )}

                  {event.location && (
                    <div className="flex items-start">
                      <MapPinIcon className="h-5 w-5 text-gray-500 mr-2 mt-0.5" />
                      <span>{event.location}</span>
                    </div>
                  )}

                  {event.price && (
                    <div className="flex items-start">
                      <CurrencyDollarIcon className="h-5 w-5 text-gray-500 mr-2 mt-0.5" />
                      <span>{event.price}</span>
                    </div>
                  )}

                  <div className="flex items-center">
                    <span className="mr-2 font-medium">Status:</span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        event.status === "open"
                          ? "bg-green-100 text-green-800"
                          : event.status === "closed"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {event.status.charAt(0).toUpperCase() +
                        event.status.slice(1)}
                    </span>
                  </div>

                  {event.stripeProductId ? (
                    <div className="flex items-center">
                      <span className="font-medium mr-2">Stripe Product:</span>
                      <span className="text-gray-600 text-xs">
                        {event.stripeProductId.substring(0, 10)}...
                      </span>
                    </div>
                  ) : (
                    parseFloat(event.price) > 0 && event.status !== "hidden" && (
                      <div className="flex items-center">
                        <span className="font-medium mr-2">Stripe Product:</span>
                        <span className="text-red-600 text-xs">❌ Missing</span>
                      </div>
                    )
                  )}
                </div>
              </div>

              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-between">
                <button
                  onClick={() => handleOpenDialog(event)}
                  className="text-gray-700 hover:text-gold flex items-center text-sm"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>

                <button
                  onClick={() => handleDeleteClick(event)}
                  className="text-red-600 hover:text-red-800 flex items-center text-sm"
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  Delete
                </button>

                <button
                  onClick={() => handleOpenAttendeesDialog(event)}
                  className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                >
                  <UserGroupIcon className="h-4 w-4 mr-1" />
                  Attendees
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Event Dialog */}
      {openDialog && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3
                      className="text-lg leading-6 font-medium text-gray-900"
                      id="modal-title"
                    >
                      {selectedEvent ? "Edit Event" : "Add New Event"}
                    </h3>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-6">
                      <div className="md:col-span-7 space-y-4">
                        {/* Title */}
                        <div>
                          <label
                            htmlFor="title"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Title
                          </label>
                          <input
                            type="text"
                            id="title"
                            value={formData.title}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                title: e.target.value,
                              })
                            }
                            className={`mt-1 block w-full rounded-md shadow-sm ${
                              formErrors.title
                                ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                                : "border-gray-300 focus:ring-gold focus:border-gold"
                            }`}
                            required
                          />
                          {formErrors.title && (
                            <p className="mt-1 text-sm text-red-600">
                              {formErrors.title}
                            </p>
                          )}
                        </div>

                        {/* Date */}
                        <div>
                          <label
                            htmlFor="date"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Date
                          </label>
                          <input
                            type="date"
                            id="date"
                            value={formData.date}
                            onChange={(e) =>
                              setFormData({ ...formData, date: e.target.value })
                            }
                            className={`mt-1 block w-full rounded-md shadow-sm ${
                              formErrors.date
                                ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                                : "border-gray-300 focus:ring-gold focus:border-gold"
                            }`}
                            required
                          />
                          {formErrors.date && (
                            <p className="mt-1 text-sm text-red-600">
                              {formErrors.date}
                            </p>
                          )}
                        </div>

                        {/* Time */}
                        <div>
                          <label
                            htmlFor="time"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Time
                          </label>
                          <input
                            type="time"
                            id="time"
                            value={formData.time}
                            onChange={(e) =>
                              setFormData({ ...formData, time: e.target.value })
                            }
                            className="mt-1 block w-full rounded-md shadow-sm border-gray-300 focus:ring-gold focus:border-gold"
                          />
                        </div>

                        {/* Location */}
                        <div>
                          <label
                            htmlFor="location"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Location
                          </label>
                          <input
                            type="text"
                            id="location"
                            value={formData.location}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                location: e.target.value,
                              })
                            }
                            className="mt-1 block w-full rounded-md shadow-sm border-gray-300 focus:ring-gold focus:border-gold"
                            placeholder="e.g., Sydney Olympic Park"
                          />
                        </div>

                        {/* Location URL */}
                        <div>
                          <label
                            htmlFor="locationURL"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Location URL
                          </label>
                          <input
                            type="url"
                            id="locationURL"
                            value={formData.locationURL}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                locationURL: e.target.value,
                              })
                            }
                            className="mt-1 block w-full rounded-md shadow-sm border-gray-300 focus:ring-gold focus:border-gold"
                            placeholder="e.g., https://maps.google.com/..."
                          />
                        </div>

                        {/* Price */}
                        <div>
                          <label
                            htmlFor="price"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Price
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">
                                $
                              </span>
                            </div>
                            <input
                              type="text"
                              id="price"
                              value={formData.price}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  price: e.target.value,
                                })
                              }
                              className="block w-full pl-7 pr-12 rounded-md border-gray-300 focus:ring-gold focus:border-gold"
                              placeholder="0.00"
                              aria-describedby="price-currency"
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                              <span
                                className="text-gray-500 sm:text-sm"
                                id="price-currency"
                              >
                                AUD
                              </span>
                            </div>
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Enter 0 for free events
                          </p>
                        </div>

                        {/* Status */}
                        <div>
                          <label
                            htmlFor="status"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Status
                          </label>
                          <select
                            id="status"
                            value={formData.status}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                status: e.target.value,
                              })
                            }
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-gold focus:border-gold"
                          >
                            <option value="open">
                              Open (Accepting Registrations)
                            </option>
                            <option value="closed">
                              Closed (Not Accepting Registrations)
                            </option>
                            <option value="hidden">
                              Hidden (Not Visible to Users)
                            </option>
                          </select>
                        </div>
                      </div>

                      <div className="md:col-span-5 space-y-4">
                        {/* Description with formatting toolbar */}
                        <div>
                          <label
                            htmlFor="description"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Description
                          </label>

                          <div className="mt-1 border border-gray-300 rounded-md overflow-hidden">
                            {/* Formatting toolbar */}
                            <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border-b border-gray-300">
                              <button
                                type="button"
                                onClick={formatBold}
                                className="p-1.5 rounded hover:bg-gray-200 text-gray-700"
                                title="Bold"
                              >
                                <span className="font-bold">B</span>
                              </button>

                              <button
                                type="button"
                                onClick={formatItalic}
                                className="p-1.5 rounded hover:bg-gray-200 text-gray-700"
                                title="Italic"
                              >
                                <span className="italic">I</span>
                              </button>

                              <button
                                type="button"
                                onClick={formatHeading}
                                className="p-1.5 rounded hover:bg-gray-200 text-gray-700"
                                title="Heading"
                              >
                                <DocumentTextIcon className="h-4 w-4" />
                              </button>

                              <div className="h-6 mx-1 border-l border-gray-300"></div>

                              <button
                                type="button"
                                onClick={insertBulletList}
                                className="p-1.5 rounded hover:bg-gray-200 text-gray-700"
                                title="Bullet List"
                              >
                                <ListBulletIcon className="h-4 w-4" />
                              </button>

                              <button
                                type="button"
                                onClick={insertNumberedList}
                                className="p-1.5 rounded hover:bg-gray-200 text-gray-700"
                                title="Numbered List"
                              >
                                <QueueListIcon className="h-4 w-4" />
                              </button>

                              <div className="h-6 mx-1 border-l border-gray-300"></div>

                              <button
                                type="button"
                                onClick={insertLink}
                                className="p-1.5 rounded hover:bg-gray-200 text-gray-700"
                                title="Insert Link"
                              >
                                <LinkIcon className="h-4 w-4" />
                              </button>

                              <button
                                type="button"
                                onClick={insertImage}
                                className="p-1.5 rounded hover:bg-gray-200 text-gray-700"
                                title="Insert Image"
                              >
                                <PhotoIcon className="h-4 w-4" />
                              </button>
                            </div>

                            <textarea
                              ref={textareaRef}
                              id="description"
                              value={formData.description}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  description: e.target.value,
                                })
                              }
                              className={`block w-full border-0 focus:ring-0 min-h-[200px] p-3 ${
                                formErrors.description ? "bg-red-50" : ""
                              }`}
                              placeholder="Enter event description here..."
                            />
                          </div>

                          {formErrors.description && (
                            <p className="mt-1 text-sm text-red-600">
                              {formErrors.description}
                            </p>
                          )}
                          <p className="mt-1 text-xs text-gray-500">
                            You can use the toolbar to format text or add links
                            and images.
                          </p>
                        </div>

                        {/* Image Upload */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Event Image
                          </label>
                          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                            <div className="space-y-1 text-center">
                              {previewUrl ? (
                                <div className="mb-3">
                                  <img
                                    src={previewUrl}
                                    alt="Event preview"
                                    className="mx-auto h-32 w-auto object-cover rounded-md"
                                  />
                                </div>
                              ) : (
                                <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                              )}

                              <div className="flex text-sm text-gray-600">
                                <label
                                  htmlFor="file-upload"
                                  className="relative cursor-pointer bg-white rounded-md font-medium text-gold hover:text-gold/80 focus-within:outline-none"
                                >
                                  <span>Upload a file</span>
                                  <input
                                    id="file-upload"
                                    name="file-upload"
                                    type="file"
                                    className="sr-only"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                  />
                                </label>
                                <p className="pl-1">or drag and drop</p>
                              </div>
                              <p className="text-xs text-gray-500">
                                PNG, JPG, GIF up to 10MB
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gold text-base font-medium text-white hover:bg-gold/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading && (
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  {selectedEvent ? "Update Event" : "Create Event"}
                </button>
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon
                      className="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3
                      className="text-lg leading-6 font-medium text-gray-900"
                      id="modal-title"
                    >
                      Delete Event
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this event? This action
                        cannot be undone. All data associated with this event
                        will be permanently removed.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={handleDeleteCancel}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attendees Dialog */}
      {attendeesDialogOpen && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <div className="flex items-center space-x-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Event Attendees
                    </h3>
                    {attendees.length > 0 && (
                      <button
                        type="button"
                        onClick={handleOpenEmailDialog}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-gold hover:bg-gold/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold"
                      >
                        <EnvelopeIcon className="h-4 w-4 mr-1.5" />
                        Email Attendees
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleCloseAttendeesDialog}
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-4">
                  {attendeesLoading ? (
                    <div className="flex justify-center py-8">
                      <ArrowPathIcon className="h-8 w-8 text-gold animate-spin" />
                    </div>
                  ) : attendees.length === 0 ? (
                    <p className="text-center py-8 text-gray-500">
                      No attendees found for this event.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Name
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Email
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Company
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Purchase Date
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Status
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Attendance
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {attendees.map((ticket) => (
                            <tr key={ticket.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {ticket.name || "N/A"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {ticket.email || "N/A"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {ticket.company || "N/A"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {ticket.purchaseDate
                                  ? new Date(
                                      ticket.purchaseDate
                                    ).toLocaleDateString()
                                  : "N/A"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    ticket.status === "active"
                                      ? "bg-green-100 text-green-800"
                                      : ticket.status === "used"
                                      ? "bg-gray-100 text-gray-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  {ticket.status.charAt(0).toUpperCase() +
                                    ticket.status.slice(1)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <button
                                  onClick={() =>
                                    toggleAttendance(
                                      ticket.id,
                                      ticket.attended || false
                                    )
                                  }
                                  className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded ${
                                    ticket.attended
                                      ? "bg-green-100 text-green-800 hover:bg-green-200"
                                      : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                                  }`}
                                >
                                  {ticket.attended ? (
                                    <>
                                      <CheckIcon className="h-4 w-4 mr-1" />
                                      Attended
                                    </>
                                  ) : (
                                    <>
                                      <XMarkIcon className="h-4 w-4 mr-1" />
                                      Not Attended
                                    </>
                                  )}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleCloseAttendeesDialog}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Dialog */}
      {emailDialogOpen && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Send Email to Attendees
                  </h3>
                  <button
                    type="button"
                    onClick={handleCloseEmailDialog}
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-4">
                  {emailLoading ? (
                    <div className="flex justify-center py-8">
                      <ArrowPathIcon className="h-8 w-8 text-gold animate-spin" />
                    </div>
                  ) : (
                    <>
                      {emailError && (
                        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md">
                          {emailError}
                        </div>
                      )}
                      {emailSuccess && (
                        <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-md">
                          {emailSuccess}
                        </div>
                      )}
                      <div>
                        <label
                          htmlFor="email-title"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Email Title
                        </label>
                        <input
                          type="text"
                          id="email-title"
                          value={emailData.title}
                          onChange={(e) =>
                            setEmailData({
                              ...emailData,
                              title: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md shadow-sm border-gray-300 focus:ring-gold focus:border-gold text-base py-3 px-4"
                          placeholder="Enter email title..."
                        />
                      </div>
                      <div className="mt-6">
                        <label
                          htmlFor="email-message"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Email Message
                        </label>
                        <textarea
                          id="email-message"
                          value={emailData.message}
                          onChange={(e) =>
                            setEmailData({
                              ...emailData,
                              message: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md shadow-sm border-gray-300 focus:ring-gold focus:border-gold text-base py-3 px-4 min-h-[300px] resize-y"
                          placeholder="Enter email message here..."
                        />
                        <p className="mt-2 text-sm text-gray-500">
                          You can use line breaks to format your message. The email will be sent to all attendees of this event.
                        </p>
                      </div>
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={handleSendEmail}
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gold text-base font-medium text-white hover:bg-gold/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold sm:w-auto sm:text-sm"
                        >
                          Send Email
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventManagement;
